import { Defaults } from "@/models/Defaults";
import { InsuranceAddress } from "@/models/InsuranceAddress";
import { Quote } from "@/models/Quote";
import { PaymentOptions } from "@/models/QuoteInsurance";
import { DateTimeFormatter, LocalDate } from "@js-joda/core";
import { computed } from "vue";

export function maskedCardNumber(cardNumber: string): string {
  return `${"*".repeat(cardNumber.length - 4)}${cardNumber.substr(
    cardNumber.length - 4
  )}`;
}

export function nth(d: number) {
  if (d > 3 && d < 21) return "th";
  switch (d % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

export function billingDayWithOrdinal(billingDay: number): string {
  return billingDay + nth(billingDay);
}

export function formatAmountWithoutCents(value: number): string {
  const num = Number(value);
  // Create our number formatter.
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  return num != undefined ? formatter.format(num) : "N/A";
}

export function formatAmount(value: any): string {
  const num = Number(value);
  // Create our number formatter.
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  return num != undefined ? formatter.format(num) : "N/A";
  //return num ? formatter.format(num) : "N/A";  ----this is fo QuoteChartMobile and QuoteAccount
}

export function formatNssiCoverageAmount(value: any): string {
  value = parseInt(value);
  return String(value);
}

/**
 * Formats insuranceaddress
 * @param address
 */
export function formatAddress(address: InsuranceAddress): string {
  return (
    address.streetNumber +
    " " +
    address.address +
    (address.apt ? ", Apt " + address.apt : "") +
    ", " +
    address.city +
    ", " +
    address.state.short +
    " " +
    address.zip
  );
}

export function cardTypeShort(cardNumber: string) {
  // visa
  let re = new RegExp("^4");
  if (cardNumber.match(re) != null) return "VI";

  // Mastercard
  // Updated for Mastercard 2017 BINs expansion
  if (
    /^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/.test(
      cardNumber
    )
  )
    return "MC";

  // AMEX
  re = new RegExp("^3[47]");
  if (cardNumber.match(re) != null) return "AE";

  // Discover
  re = new RegExp(
    "^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)"
  );
  if (cardNumber.match(re) != null) return "DI";

  // Diners
  re = new RegExp("^36");
  if (cardNumber.match(re) != null) return "Diners";

  // Diners - Carte Blanche
  re = new RegExp("^30[0-5]");
  if (cardNumber.match(re) != null) return "Diners - Carte Blanche";

  // JCB
  re = new RegExp("^35(2[89]|[3-8][0-9])");
  if (cardNumber.match(re) != null) return "JCB";

  // Visa Electron
  re = new RegExp("^(4026|417500|4508|4844|491(3|7))");
  if (cardNumber.match(re) != null) return "VI";

  return "";
}

export function cardTypeLong(cardNumber: string) {
  // visa
  switch (cardTypeShort(cardNumber)) {
    case "AE":
      return "Amex";
    case "MC":
      return "Mast";
    case "VI":
      return "Visa";
    case "DI":
      return "Disc";
    default:
      return "";
  }
}

export function calculateQuoteTotal(quote: Quote): number {
  let result = 0;
  if (quote.hasPersonalInsurance) {
    if (quote.personalInsurance.paymentOption == PaymentOptions.Annual) {
      result += quote.personalInsurance.annualAmount + quote.processingFee;
    } else if (
      quote.personalInsurance.paymentOption == PaymentOptions.Monthly
    ) {
      result +=
        quote.personalInsurance.monthlyAmount + quote.monthlyProcessingFee;
    }
  }

  if (quote.rentersInsurance.dueToday) {
    result += quote.rentersInsurance.dueToday;
  }

  return result;
}

export function convertBooleanToYesNo(input: boolean | undefined): string {
  if (input) {
    return "Y";
  } else {
    return "N";
  }
}

export function convertYesNoToBoolean(input: string): boolean {
  if (input == "Y") {
    return true;
  } else {
    return false;
  }
}

export function rentersNextPaymentDateFormated(quote: Quote) {
  let date = undefined;

  if (quote.rentersInsurance.paymentOption == PaymentOptions.Annual) {
    return "";
  } else if (quote.rentersInsurance.paymentOption == PaymentOptions.FourPay) {
    date = LocalDate.now().plusMonths(4);
  } else if (quote.rentersInsurance.paymentOption == PaymentOptions.Monthly) {
    date = LocalDate.now().plusMonths(1);
  }
  const dateFormatted = date?.format(DateTimeFormatter.ofPattern("M/d/yyyy"));
  return dateFormatted;
}

export function nssiNextPaymentDateFormated(quote: Quote) {
  if (!quote.hasPersonalInsurance) {
    return "";
  }
  if (quote.personalInsurance.paymentOption == PaymentOptions.Annual) {
    return "";
  } else if (quote.personalInsurance.paymentOption == PaymentOptions.Monthly) {
    const date = LocalDate.now().plusMonths(1);
    const dateFormatted = date?.format(DateTimeFormatter.ofPattern("M/d/yyyy"));
    return dateFormatted;
  }
}

export function formatDateForDateInput(d: Date) {
  let month = "" + (d.getMonth() + 1);
  let day = "" + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;

  return [year, month, day].join("-");
}

export function formatPhoneNumber(phoneNumberString: string) {
  const cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return null;
}

export function formatDate(value: string) {
  if (value) {
    return LocalDate.parse(value).format(
      DateTimeFormatter.ofPattern("M/d/yyyy")
    );
  }
}

export function formatPaymentOption(value: any) {
  const num = Number(value) as PaymentOptions;
  switch (num) {
    case PaymentOptions.Monthly:
      return "Monthly";
    case PaymentOptions.Annual:
      return "Annual";
    case PaymentOptions.FourPay:
      return "4 Pay";
    default:
      return "Unknown";
  }
}

export function calculateQuoteNssi(quote: Quote): number {
  let result = 0;
  if (quote.hasPersonalInsurance) {
    if (quote.personalInsurance.paymentOption == PaymentOptions.Annual) {
      result += quote.personalInsurance.annualAmount + quote.processingFee;
    } else if (
      quote.personalInsurance.paymentOption == PaymentOptions.Monthly
    ) {
      result +=
        quote.personalInsurance.monthlyAmount + quote.monthlyProcessingFee;
    }
  }

  return result;
}

import { UserData } from "@/api/UserData";
import { useUserStore } from "@/stores/root/users";
export const checkUserStatus = async () => {
  const result = await UserData.fetchUser();
  if (result.error) {
    //stat().error(result.message);
    scrollMeTo("status");
    return true;
  } else {
    useUserStore().user.loggedIn = result.result.loggedIn;
    //login;
    return false;
  }
};

export function scrollMeTo(refName: string) {
  const element = refName as any;
  if (element) {
    const top = element.offsetTop;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}
export function scrollTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

export function stat(data: any) {
  return data as any;
}

export const minus18YearsDate = computed(() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return formatDateForDateInput(d);
});

export function removeBodyScroll() {
  document.body.style.overflowY = "hidden";
}

export function addBodyScroll() {
  document.body.style.overflowY = "visible";
}

export const getCurrentYear = computed(() => {
  return new Date().getFullYear();
});
